import Image from "next/image";
import Link from "next/link";
import { useRouter } from "next/router";
import { PlayCircle } from "lucide-react";
import { getVideoLesson } from "@/content/videoLessons";
import { trackMarketingEngagement } from "@/lib/analytics/marketingEngagement";
import {
  campaignParamsFromQuery,
  withCampaignParams,
} from "@/lib/marketing/campaignAttribution";

export function RelatedVideoLessons({
  heading = "Watch the method",
  intro,
  slugs,
}: {
  heading?: string;
  intro: string;
  slugs: string[];
}) {
  const router = useRouter();
  const campaignParams = campaignParamsFromQuery(router.query);
  const lessons = slugs.map(getVideoLesson).filter(Boolean);

  if (lessons.length === 0) return null;

  return (
    <section className="my-14" aria-labelledby="related-video-lessons-heading">
      <div className="mb-6">
        <p className="text-xs font-black uppercase tracking-[0.18em] text-indigo-600 dark:text-indigo-400">
          Short video lessons
        </p>
        <h2
          id="related-video-lessons-heading"
          className="mt-2 text-2xl font-black text-gray-900 dark:text-white"
        >
          {heading}
        </h2>
        <p className="mt-2 max-w-3xl text-gray-600 dark:text-slate-400">
          {intro}
        </p>
      </div>

      <div className="grid grid-cols-1 gap-5 sm:grid-cols-3">
        {lessons.map((lesson) => {
          if (!lesson) return null;
          return (
            <Link
              key={lesson.slug}
              href={withCampaignParams(`/videos/${lesson.slug}`, campaignParams)}
              prefetch={false}
              onClick={() =>
                trackMarketingEngagement("resource_video_click", {
                  lesson: lesson.slug,
                })
              }
              className="group overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-sm transition hover:-translate-y-1 hover:border-indigo-200 hover:shadow-lg dark:border-slate-800 dark:bg-slate-900/50 dark:hover:border-indigo-500/40"
            >
              <div className="relative aspect-[9/14] overflow-hidden bg-slate-950">
                <Image
                  src={lesson.posterPath}
                  alt=""
                  fill
                  sizes="(max-width: 640px) 100vw, 33vw"
                  className="object-cover transition duration-500 group-hover:scale-[1.02]"
                />
                <span className="absolute bottom-3 left-3 inline-flex items-center gap-1.5 rounded-full bg-black/75 px-3 py-1.5 text-xs font-bold text-white backdrop-blur">
                  <PlayCircle className="h-4 w-4" aria-hidden />
                  {lesson.durationLabel}
                </span>
              </div>
              <div className="p-4">
                <h3 className="font-black leading-snug text-gray-900 transition-colors group-hover:text-indigo-600 dark:text-white dark:group-hover:text-indigo-400">
                  {lesson.shortTitle}
                </h3>
                <p className="mt-2 text-sm font-semibold text-indigo-600 dark:text-indigo-400">
                  Watch lesson
                </p>
              </div>
            </Link>
          );
        })}
      </div>
    </section>
  );
}
